/**
 * Marketing Primitives
 *
 * Shared layout blocks for the redesigned public marketing surface.
 *
 * Primary responsibilities:
 * - Keep spacing and framing consistent across marketing routes
 * - Provide reusable hero, section, and panel patterns
 *
 * Dependencies:
 * - marketing presentation helpers
 * - lib/utils
 */

import { ComponentType, ReactNode } from 'react'

import {
  MarketingAmbientField,
  MarketingPanelMotion,
  MarketingReveal
} from '@/components/marketing/marketing-motion'
import { cn } from '@/lib/utils'

type MarketingIconComponent = ComponentType<{
  className?: string
  stroke?: number
}>

interface MarketingPageProps {
  children: ReactNode
  className?: string
}

interface MarketingContainerProps {
  children: ReactNode
  className?: string
}

interface MarketingEyebrowProps {
  children: ReactNode
  className?: string
}

interface MarketingHeroProps {
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  aside?: ReactNode
  className?: string
}

interface MarketingSectionProps {
  eyebrow?: ReactNode
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  headerClassName?: string
  contentClassName?: string
}

interface MarketingPanelProps {
  children: ReactNode
  className?: string
  motion?: 'default' | 'static'
  interactive?: boolean
}

interface MarketingStatProps {
  value: string
  label: string
  icon?: ReactNode
}

export function MarketingPage({ children, className }: MarketingPageProps) {
  // Use a div — the marketing layout already wraps page content in <main>.
  // Nesting <main> inside <main> creates duplicate landmarks on every page.
  return (
    <div
      className={cn(
        'relative isolate overflow-hidden px-4 py-24 sm:px-6 lg:px-8 lg:pt-28',
        className
      )}
    >
      <MarketingAmbientField />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[42rem] bg-[radial-gradient(circle_at_top_left,rgba(128,146,174,0.16),transparent_42%),radial-gradient(circle_at_top_right,rgba(207,190,164,0.14),transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.72),rgba(255,255,255,0))] dark:bg-[radial-gradient(circle_at_top_left,rgba(34,52,78,0.16),transparent_44%),radial-gradient(circle_at_top_right,rgba(48,62,82,0.08),transparent_38%),linear-gradient(180deg,rgba(6,10,18,0.74),rgba(6,10,18,0))]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 top-24 -z-10 bg-[linear-gradient(180deg,rgba(255,255,255,0),rgba(255,255,255,0.72)_14rem,rgba(255,255,255,0.98)_38rem,rgba(255,255,255,0.98)_100%)] dark:bg-[linear-gradient(180deg,rgba(12,18,27,0),rgba(12,18,27,0.42)_16rem,rgba(4,8,14,0.94)_38rem,rgba(4,8,14,0.94)_100%)]" />
      {children}
    </div>
  )
}

export function MarketingContainer({
  children,
  className
}: MarketingContainerProps) {
  return <div className={cn('mx-auto w-full max-w-[1400px]', className)}>{children}</div>
}

export function MarketingEyebrow({
  children,
  className
}: MarketingEyebrowProps) {
  return (
    <p
      className={cn(
        'text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400',
        className
      )}
    >
      {children}
    </p>
  )
}

export function MarketingHero({
  eyebrow,
  title,
  description,
  actions,
  aside,
  className
}: MarketingHeroProps) {
  return (
    <section
      className={cn(
        'relative grid gap-10 overflow-hidden py-4 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14',
        className
      )}
    >
      <MarketingReveal className="flex flex-col gap-5" distance={30} blurPx={12}>
        {eyebrow}
        <div className="space-y-5">
          <h1 className="font-display max-w-[14ch] text-4xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 sm:text-5xl lg:text-[4.75rem] dark:text-slate-50">
            {title}
          </h1>
          {description ? (
            <div className="max-w-[62ch] text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              {description}
            </div>
          ) : null}
        </div>
        {actions ? <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">{actions}</div> : null}
      </MarketingReveal>
      {aside ? (
        <MarketingReveal
          className="relative flex flex-col gap-4"
          delay={0.14}
          distance={36}
          blurPx={14}
        >
          {aside}
        </MarketingReveal>
      ) : null}
    </section>
  )
}

export function MarketingSection({
  eyebrow,
  title,
  description,
  children,
  className,
  headerClassName,
  contentClassName
}: MarketingSectionProps) {
  return (
    <section className={cn('relative mt-12 py-8 lg:mt-16 lg:py-12', className)}>
      {eyebrow || title || description ? (
        <MarketingReveal
          className={cn('mb-8 flex max-w-[46rem] flex-col gap-4 lg:mb-10', headerClassName)}
        >
          {eyebrow ? eyebrow : null}
          {title ? (
            <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {title}
            </h2>
          ) : null}
          {description ? (
            <div className="max-w-[40ch] text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              {description}
            </div>
          ) : null}
        </MarketingReveal>
      ) : null}
      <div className={contentClassName}>{children}</div>
    </section>
  )
}

export function MarketingPanel({
  children,
  className,
  motion = 'default',
  interactive = true
}: MarketingPanelProps) {
  // Keep a light surface only for interactive forms / controls — not for content chrome.
  const panelClassName = cn(
    'rounded-[1.75rem] border border-slate-200/80 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.04]',
    className
  )

  if (motion === 'static') {
    return <div className={panelClassName}>{children}</div>
  }

  return (
    <MarketingPanelMotion className={panelClassName} interactive={interactive}>
      {children}
    </MarketingPanelMotion>
  )
}

export function MarketingStatGrid({
  items,
  className
}: {
  items: MarketingStatProps[]
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-3',
        className
      )}
    >
      {items.map((item, index) => (
        <div
          key={`${item.label}-${item.value}`}
          className={cn(
            'flex flex-col gap-1 py-6 sm:px-6',
            index > 0 && 'sm:border-l sm:border-slate-200/80 dark:sm:border-white/10'
          )}
        >
          <div className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
            {item.icon}
            {item.value}
          </div>
          <div className="text-sm leading-6 text-slate-600 dark:text-slate-300">{item.label}</div>
        </div>
      ))}
    </div>
  )
}

/** Icon marker for feature / audience rails (preferred over 01/02/03 everywhere). */
export function MarketingRailIcon({
  icon: Icon,
  tone = 'default',
  size = 'md',
  className
}: {
  icon: MarketingIconComponent
  tone?: 'default' | 'on-dark'
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-xl',
        size === 'sm' ? 'size-8' : 'size-10',
        tone === 'on-dark'
          ? 'bg-white/10 text-slate-200'
          : 'bg-[rgb(236,240,247)] text-[rgb(60,82,110)] dark:bg-sky-500/15 dark:text-sky-200',
        className
      )}
    >
      <Icon className={size === 'sm' ? 'size-4' : 'size-5'} stroke={1.5} />
    </span>
  )
}
